import { useMassActionsContext } from '@components/MassActions/contexts/MassActions.context';
import { useListMassActions } from '@components/MassActions/hooks/useListMassActions';
import { useState } from 'react';
import { ActionHandler } from '../../../../../components/MultipleActionsInterfaces';
import { CatalogProductsTestIds } from '../../../../../utils/testIds/CatalogProducts.testIds';
import type { CatalogMoveToSectionTarget } from '../components/CatalogMoveToSectionModal/catalogMoveToSectionModal.types';
import type { CatalogSelectionItem } from '../contexts/CatalogMassActions.context';
import { CatalogMassActionType, buildCatalogMassActions } from '../utils/catalogMassActions.utils';
import { CatalogViewGroupBy } from '../view/catalogView.types';
import { useCatalogEffectiveView } from './useCatalogEffectiveView';
import { useCatalogMassActionOperations } from './useCatalogMassActionOperations';
import type {
  UseCatalogMassActionsParams,
  UseCatalogMassActionsResult,
} from './useCatalogMassActions.types';

export const useCatalogMassActions = ({
  readMode,
  totalItems,
  isDynamic,
  catalogId,
  canPersist,
  selection,
}: UseCatalogMassActionsParams): UseCatalogMassActionsResult => {
  const { massActionFilter, toggleMassActionFilter } = useMassActionsContext();
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);

  const operations = useCatalogMassActionOperations({ catalogId, canPersist });
  const { groupBy, isSettled } = useCatalogEffectiveView({ catalogId, canPersist });

  const { selectedItems, selecteds, isSelected, toggleSelected, clearSelection } = selection;

  const toggle = (item: CatalogSelectionItem) => {
    if (massActionFilter) {
      toggleMassActionFilter();
    }

    toggleSelected(item);
  };

  const handleMassAction: ActionHandler<CatalogMassActionType> = async (action) => {
    if (action.type === 'remove_batch') {
      const isRemoved = await operations.remove(selecteds);
      if (isRemoved) {
        clearSelection();
      }
      return;
    }

    if (action.type === 'move_to_section_batch') {
      setIsMoveModalOpen(true);
    }
  };

  const resolveItemIdsToMove = () => {
    if (massActionFilter) {
      return [];
    }

    return isDynamic ? [...selectedItems.values()] : selecteds;
  };

  const handleMoveConfirm = async ({ sectionId, sectionName }: CatalogMoveToSectionTarget) => {
    const isMoved = await operations.moveToSection({
      itemIds: resolveItemIdsToMove(),
      sectionId,
      sectionName,
      isDynamic,
    });

    if (isMoved) {
      setIsMoveModalOpen(false);
      clearSelection();

      if (massActionFilter) {
        toggleMassActionFilter();
      }
    }
  };

  useListMassActions<CatalogMassActionType, CatalogSelectionItem>({
    list: {
      state: { selecteds: readMode ? [] : selecteds },
      checkbox: { isAllItemsSelected: false, toggleSelectAll: clearSelection },
    },
    actions: buildCatalogMassActions({
      isManualGroupingEffective: isSettled && groupBy === CatalogViewGroupBy.MANUAL,
      isDynamic,
      isSelectAllByFilter: massActionFilter,
    }),
    onClickAction: handleMassAction,
    totalResults: readMode ? undefined : totalItems,
    isAllSelected: massActionFilter,
    toggleAllSelected: clearSelection,
    testId: CatalogProductsTestIds.MASS_ACTIONS_BAR,
  });

  const totalSelected = massActionFilter ? totalItems : selecteds.length;

  return {
    contextValue: {
      readMode,
      isSelectionActive: !readMode && totalSelected > 0,
      isSelectionLocked: massActionFilter,
      isSelected: (productId: string) => massActionFilter || isSelected(productId),
      toggle,
    },
    moveModal: {
      isOpen: isMoveModalOpen,
      catalogId,
      onClose: () => setIsMoveModalOpen(false),
      onConfirm: handleMoveConfirm,
    },
  };
};
